import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, vShow as _vShow, withKeys as _withKeys, withModifiers as _withModifiers, normalizeClass as _normalizeClass, withCtx as _withCtx, withDirectives as _withDirectives, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import { computed, ref, watch } from "vue";
import { MapBoxFeature, MapBoxFeatureCollection, textForMapboxFeature } from "@cosmicds/vue-toolkit/src/mapbox";

type SearchProvider = (searchText: string) => Promise<MapBoxFeatureCollection | null>;

export interface LocationSearchProps {
  searchProvider?: SearchProvider;
  modelValue?: boolean;
  stayOpen?: boolean;
  accentColor?: string;
  small?: boolean;
  theme?: string;
  buttonSize?: string;
  bgColor?: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'LocationSearch',
  props: {
    searchProvider: { type: Function, default: (_searchText: string) => null },
    modelValue: { type: Boolean, default: true },
    stayOpen: { type: Boolean, default: false },
    accentColor: { default: "white" },
    small: { type: Boolean, default: false },
    theme: { default: "dark" },
    buttonSize: { default: "1x" },
    bgColor: { default: "black" }
  },
  emits: ["error", "set-location", "update:modelValue"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const searchOpen = ref(props.modelValue || props.stayOpen);
const searchItem = ref<string | null>(null);
const searchResults = ref<MapBoxFeatureCollection | null>(null);
const searchErrorMessage = ref<string | null>(null);
const locationJustUpdated = ref(false);
const menuOpen = ref(false);
const comboFocused = ref(false);
const searchInput = ref<HTMLInputElement | null>(null);

const cssStyles = computed(() => {
  return {
    "--accent-color": props.accentColor,
    "--bg-color": props.bgColor,
    "--fg-container-padding": searchOpen.value ? (props.small ? "0px 5px 0px 0px" : "5px 10px 12px 10px") : "0px",
    "--border-radius": searchOpen.value ? "7px" : "20px",
  };
});

function itemText(item: string | MapBoxFeature): string {
  if (typeof item === "string") {
    return item;
  }
  return textForMapboxFeature(item);
}

function performForwardGeocodingSearch() {
  const search = searchItem.value;
  if (search === null || search.length < 3) {
    return;
  }

  props.searchProvider(search).then(info => {
    if (info !== null && info.features.length === 1) {
      setLocationFromSearchFeature(info.features[0]);
    } else if (info !== null && info.features.length === 0) {
      searchErrorMessage.value = "No matching places were found";
      emit("error", searchErrorMessage.value); 
    } else {
      searchResults.value = info;
    }
  });
}

function focusCombobox() {
  const input = searchInput.value as HTMLInputElement;
  input.focus();
}

function blurCombobox() {
  const input = searchInput.value as HTMLInputElement;
  input.blur();
}

function toggleSearch() {
  if (searchOpen.value) {
    performForwardGeocodingSearch();
    menuOpen.value = true;
    focusCombobox();
  } else {
    searchOpen.value = true;
  }
}

// function closeSearch() {
//   searchOpen.value = false;
//   clearSearchData();
// }

function onFocusChange(focused: boolean) {
  comboFocused.value = focused;
}

function setLocationFromSearchFeature(feature: MapBoxFeature) {
  blurCombobox();
  timedJustUpdatedLocation();
  clearSearchData();
  emit("set-location", feature);
}

function clearSearchData() {
  searchResults.value = null;
  searchItem.value = null;
  searchErrorMessage.value = null;
}

function timedJustUpdatedLocation() {
  locationJustUpdated.value = true;
  setTimeout(() => {
    locationJustUpdated.value = false;
  }, 5000);
}

watch(() => props.modelValue, (value: boolean) => { searchOpen.value = value; });

watch(searchOpen, (value: boolean) => emit("update:modelValue", value));

watch(searchItem, function(item: string | MapBoxFeature | null) {
  if (searchErrorMessage.value) {
    searchErrorMessage.value = null;
  }
  if (!item || (typeof item === "string" && item.length === 0)) {
    searchResults.value = null;
  }
  if (item && typeof item !== "string") {
    setLocationFromSearchFeature(item);
  }
});

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!
  const _component_v_combobox = _resolveComponent("v-combobox")!

  return (_openBlock(), _createElementBlock("div", {
    class: "forward-geocoding-container",
    style: _normalizeStyle(cssStyles.value)
  }, [
    _withDirectives(_createVNode(_component_v_combobox, {
      class: _normalizeClass(['forward-geocoding-input', locationJustUpdated.value ? 'geocode-success' : '', _ctx.small ? 'forward-geocoding-input-small' : '']),
      modelValue: searchItem.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchItem).value = $event)),
      items: searchResults.value ? searchResults.value.features : [],
      "item-title": itemText,
      "bg-color": _ctx.bgColor,
      label: "Enter city or zip",
      density: _ctx.small ? 'compact' : 'default',
      "hide-details": "",
      solo: "",
      color: _ctx.accentColor,
      onInput: () => {},
      onKeydown: [
        _withKeys(performForwardGeocodingSearch, ["enter"]),
        _cache[1] || (_cache[1] = _withKeys(($event: any) => (searchResults.value = null), ["esc"])),
        _cache[2] || (_cache[2] = _withModifiers(() => {}, ["stop"]))
      ],
      "error-messages": searchErrorMessage.value,
      "onClick:append": focusCombobox,
      "onUpdate:focused": _cache[3] || (_cache[3] = ($event: any) => (onFocusChange($event))),
      ref_key: "searchInput",
      ref: searchInput,
      menu: menuOpen.value,
      "onUpdate:menu": _cache[4] || (_cache[4] = ($event: any) => (menuOpen.value = $event))
    }, {
      append: _withCtx(() => [
        _createVNode(_component_font_awesome_icon, {
          class: "geocoding-search-icon",
          icon: "magnifying-glass",
          size: searchOpen.value ? 'xl' : _ctx.buttonSize,
          color: "gray",
          onClick: toggleSearch
        }, null, 8, ["size"])
      ]),
      _: 1
    }, 8, ["class", "modelValue", "items", "bg-color", "density", "color", "error-messages", "menu"]), [
      [_vShow, searchOpen.value]
    ]),
    _withDirectives(_createVNode(_component_font_awesome_icon, {
      class: "geocoding-search-icon",
      icon: "magnifying-glass",
      size: searchOpen.value ? 'xl' : _ctx.buttonSize,
      color: "gray",
      onClick: _withModifiers(toggleSearch, ["prevent"])
    }, null, 8, ["size"]), [
      [_vShow, !searchOpen.value && !_ctx.stayOpen]
    ])
  ], 4))
}
}

})